import React, { memo } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ViewTypeIcon from './ViewTypeIcon';
import AddViewMenu from './AddViewMenu';

/**
 * SortableViewTab - Individual sortable view tab using dnd-kit
 */
const SortableViewTab = memo(({
  view,
  isActive,
  canDelete,
  editingViewId,
  editingName,
  editInputRef,
  onCustomViewClick,
  onCustomViewContextMenu,
  onEditingNameChange,
  onSaveEditedName,
  onEditKeyDown,
  onDeleteClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: view.id,
    disabled: editingViewId === view.id, // Disable dragging while editing
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`view-bar__tab ${isActive ? 'view-bar__tab--active' : ''} ${isDragging ? 'view-bar__tab--dragging' : ''}`}
      onClick={() => onCustomViewClick(view)}
      onContextMenu={(e) => onCustomViewContextMenu(e, view)}
      title={view.name}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onCustomViewClick(view)}
      {...attributes}
      {...listeners}
    >
      {editingViewId === view.id ? (
        <input
          ref={editInputRef}
          type="text"
          className="view-bar__tab-edit"
          value={editingName}
          onChange={(e) => onEditingNameChange(e.target.value)}
          onBlur={onSaveEditedName}
          onKeyDown={onEditKeyDown}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <>
          <ViewTypeIcon type={view.type} size={15} />
          <span>{view.name}</span>
          {canDelete && (
            <button
              className="view-bar__tab-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(e, view);
              }}
              title="Delete view"
              onPointerDown={(e) => e.stopPropagation()} // Prevent drag when clicking delete
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
});

SortableViewTab.displayName = 'SortableViewTab';

/**
 * ViewBar - The tab bar containing all views and add button
 * 
 * UNIFIED VIEW MODEL:
 * All views come from the customViews array. There's no distinction between
 * "default" and "custom" views - all are equal and deletable.
 * 
 * The only restriction is that at least one view must remain (can't delete
 * the last view).
 * 
 * Uses @dnd-kit for smooth drag-and-drop reordering of views.
 */
const ViewBar = ({
  customViews,
  activeCustomViewId,
  editingViewId,
  editingName,
  editInputRef,
  addMenuRef,
  isAddMenuOpen,
  onCustomViewClick,
  onCustomViewContextMenu,
  onEditingNameChange,
  onSaveEditedName,
  onEditKeyDown,
  onDeleteClick,
  onToggleAddMenu,
  onAddView,
  onReorderViews,
  // Legacy props (kept for compatibility during transition)
  currentView,
  isDefaultViewActive,
  onDefaultViewClick,
  isMultiCampaignMode = false,
  activeView = null,
}) => {
  // Determine which view is active
  // If activeCustomViewId is set, that view is active
  // Otherwise, find the first view matching currentView type (for backwards compat)
  const getActiveViewId = () => {
    if (activeCustomViewId && customViews.some(v => v.id === activeCustomViewId)) {
      return activeCustomViewId;
    }
    // Stale id (or none): highlight the tab that matches the canvas.
    const matchingView = customViews.find(v => v.type === currentView);
    return matchingView?.id || customViews[0]?.id || null;
  };
  
  const effectiveActiveId = getActiveViewId();

  // dnd-kit sensors - only pointer for horizontal tab reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    })
  );

  // Handle drag end - reorder views
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id && onReorderViews) {
      const oldIndex = customViews.findIndex(v => v.id === active.id);
      const newIndex = customViews.findIndex(v => v.id === over.id);
      const newOrder = arrayMove(customViews.map(v => v.id), oldIndex, newIndex);
      onReorderViews(newOrder);
    }
  };

  const viewIds = customViews.map(v => v.id);

  return (
    <div className="view-bar">
      <div className="view-bar__tabs">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={viewIds} strategy={horizontalListSortingStrategy}>
            {customViews.map(view => {
              const isActive = view.id === effectiveActiveId;
              const canDelete = customViews.length > 1;
              
              return (
                <SortableViewTab
                  key={view.id}
                  view={view}
                  isActive={isActive}
                  canDelete={canDelete}
                  editingViewId={editingViewId}
                  editingName={editingName}
                  editInputRef={editInputRef}
                  onCustomViewClick={onCustomViewClick}
                  onCustomViewContextMenu={onCustomViewContextMenu}
                  onEditingNameChange={onEditingNameChange}
                  onSaveEditedName={onSaveEditedName}
                  onEditKeyDown={onEditKeyDown}
                  onDeleteClick={onDeleteClick}
                />
              );
            })}
          </SortableContext>
        </DndContext>

        {/* Add View Button */}
        <div className="view-bar__add-wrapper" ref={addMenuRef}>
          <button
            className="view-bar__add-btn"
            onClick={onToggleAddMenu}
            title="Add view"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <AddViewMenu isOpen={isAddMenuOpen} onAddView={onAddView} />
        </div>
      </div>
    </div>
  );
};

export default ViewBar;
